import { zodResolver } from "@hookform/resolvers/zod";
import {
  Link,
  useRouter,
  Stack,
  useLocalSearchParams,
  type Href,
} from "expo-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { ScrollView, Text, View } from "react-native";

import { ScreenContainer } from "@/shared/components/ui/screen-container";

import { BackButton } from "@/shared/components/ui/back-button";
import { Button } from "@/shared/components/ui/button";
import { FormInput } from "@/shared/components/ui/form-input";
import { Heading } from "@/shared/components/ui/heading";
import { LoginFormData, loginSchema } from "@/features/auth/schemas/auth";
import { useLogin } from "@/features/auth/api/auth";

export default function LoginScreen() {
  const router = useRouter();
  const { redirect } = useLocalSearchParams<{ redirect?: string }>();
  const { control, handleSubmit } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });
  const { mutate: login, isPending } = useLogin();
  const [loginError, setLoginError] = useState<string | null>(null);
  const redirectPath = redirect ? `?${encodeURIComponent(redirect)}` : "";

  const onSubmit = (data: LoginFormData) => {
    setLoginError(null);
    login(data, {
      onSuccess: () => {
        router.replace((redirect ?? "/") as Href);
      },
      onError: () => {
        setLoginError("E-mail ou senha incorretos. Tente novamente.");
      },
    });
  };

  return (
    <>
      <Stack.Screen options={{ title: "Login", headerShown: false }} />
      <ScreenContainer style={{ paddingTop: 0 }}>
        <ScrollView
          className="flex-1"
          contentContainerClassName="grow"
          keyboardShouldPersistTaps="handled"
        >
          <View className="p-6">
            <BackButton
              className="mt-8 mb-8"
              onPress={() => router.back()}
              accessibilityLabel="Voltar"
            />

            <Text className="text-muted-foreground mb-2 text-sm">
              Bem-vinda de volta
            </Text>

            <Heading variant="screen" className="mb-6">
              Faça seu login
            </Heading>

            <FormInput
              control={control}
              name="email"
              label="E-mail"
              placeholder="seuemail@exemplo.com"
              keyboardType="email-address"
              autoComplete="email"
            />

            <FormInput
              control={control}
              name="password"
              label="Senha"
              placeholder="Sua senha"
              secureTextEntry
              showPasswordToggle
              autoComplete="password"
            />

            {loginError && (
              <Text className="text-destructive mb-2 text-sm">
                {loginError}
              </Text>
            )}

            <Button
              size="lg"
              className="mt-4"
              onPress={handleSubmit(onSubmit)}
              loading={isPending}
              type="submit"
            >
              Entrar
            </Button>

            <View className="mt-4 items-center">
              <Link href="/forgot-password" asChild>
                <Text className="text-brand text-sm font-semibold">
                  Esqueci minha senha
                </Text>
              </Link>
            </View>

            <View className="mt-6 flex-row items-center justify-center gap-2">
              <Text className="text-muted-foreground text-sm">
                Não tem uma conta?
              </Text>
              <Link href={`/signup${redirectPath}` as Href} asChild>
                <Text className="text-brand text-sm font-semibold">
                  Cadastre-se
                </Text>
              </Link>
            </View>
          </View>
        </ScrollView>
      </ScreenContainer>
    </>
  );
}
